import { Add, Delete, Edit, Dns, Visibility, VisibilityOff, HelpOutline } from '@mui/icons-material';
import {
    Box, Button, Card, CardContent, Container, Dialog, DialogActions,
    DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem,
    Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Tooltip, Typography, InputAdornment,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SapEnvironment } from '@/domain/entities';
import { ApiError } from '@/infrastructure/services/api-client';
import { useNotificationStore } from '@/stores/use-notification-store';
import { useSapEnvironmentsStore } from '@/stores/use-sap-environments-store';
import { useThemeStore } from '@/stores/use-theme-store';

interface FormData {
    identifier: string;
    description: string;
    host: string;
    client: string;
    username: string;
    password: string;
    language: string;
}

const initialForm: FormData = {
    identifier: '',
    description: '',
    host: '',
    client: '',
    username: '',
    password: '',
    language: 'EN',
};

const IDENTIFIER_REGEX = /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)*$/;

function isValidIdentifier(value: string): boolean {
    return IDENTIFIER_REGEX.test(value);
}

export function SapEnvironmentsPage() {
    const { t } = useTranslation();
    const { environments, isLoading, fetchEnvironments, createEnvironment, updateEnvironment, deleteEnvironment } = useSapEnvironmentsStore();
    const { addNotification } = useNotificationStore();
    const { mode } = useThemeStore();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [form, setForm] = useState<FormData>(initialForm);
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);

    useEffect(() => {
        void fetchEnvironments();
    }, [fetchEnvironments]);

    const updateForm = (field: keyof FormData, value: string) => {
        if (field === 'identifier') {
            value = value.toUpperCase().replace(/[^A-Z0-9_]/g, '');
        }
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const identifierError = form.identifier.length > 0 && !isValidIdentifier(form.identifier);
    const isFormValid = form.identifier && !identifierError && form.description && form.host && form.client && form.username && (editingId || form.password);

    const openCreate = () => {
        setEditingId(null);
        setForm(initialForm);
        setShowPassword(false);
        setDialogOpen(true);
    };

    const openEdit = (env: SapEnvironment) => {
        setEditingId(env.id);
        setForm({
            identifier: env.identifier,
            description: env.description,
            host: env.host,
            client: env.client,
            username: env.username,
            password: '',
            language: env.language || 'EN',
        });
        setShowPassword(false);
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!isFormValid) {
            return;
        }
        setSaving(true);
        try {
            if (editingId) {
                const updateData: Record<string, string> = {};
                if (form.identifier) {
                    updateData['identifier'] = form.identifier;
                }
                if (form.description) {
                    updateData['description'] = form.description;
                }
                if (form.host) {
                    updateData['host'] = form.host;
                }
                if (form.client) {
                    updateData['client'] = form.client;
                }
                if (form.username) {
                    updateData['username'] = form.username;
                }
                if (form.password) {
                    updateData['password'] = form.password;
                }
                if (form.language) {
                    updateData['language'] = form.language;
                }
                await updateEnvironment(editingId, updateData);
                addNotification(t('sapEnvironments.updated'), 'success');
            } else {
                await createEnvironment(form);
                addNotification(t('sapEnvironments.created'), 'success');
            }
            setDialogOpen(false);
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            } else {
                addNotification(t('errors.generic'), 'error');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirmId) {
            return;
        }
        try {
            await deleteEnvironment(deleteConfirmId);
            addNotification(t('sapEnvironments.deleted'), 'success');
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            } else {
                addNotification(t('errors.generic'), 'error');
            }
        } finally {
            setDeleteConfirmId(null);
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {t('sapEnvironments.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {t('sapEnvironments.subtitle')}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={openCreate}
                    sx={{ borderRadius: 3, fontWeight: 600 }}
                >
                    {t('sapEnvironments.addEnvironment')}
                </Button>
            </Box>

            <Card sx={{
                border: '1px solid',
                borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
            }}>
                <CardContent sx={{ p: 0 }}>
                    {environments.length === 0 && !isLoading ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Dns sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                {t('sapEnvironments.empty')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {t('sapEnvironments.emptyDescription')}
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('sapEnvironments.fields.identifier')}</TableCell>
                                        <TableCell>{t('sapEnvironments.fields.description')}</TableCell>
                                        <TableCell>{t('sapEnvironments.fields.host')}</TableCell>
                                        <TableCell>{t('sapEnvironments.fields.client')}</TableCell>
                                        <TableCell>{t('sapEnvironments.fields.language')}</TableCell>
                                        <TableCell>{t('sapEnvironments.fields.username')}</TableCell>
                                        <TableCell align="right">{t('common.actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {environments.map((env) => (
                                        <TableRow key={env.id} hover>
                                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{env.identifier}</TableCell>
                                            <TableCell>{env.description}</TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{env.host}</TableCell>
                                            <TableCell>{env.client}</TableCell>
                                            <TableCell>{env.language}</TableCell>
                                            <TableCell>{env.username}</TableCell>
                                            <TableCell align="right">
                                                <Tooltip title={t('common.edit')}>
                                                    <IconButton size="small" onClick={() => openEdit(env)}>
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('common.delete')}>
                                                    <IconButton size="small" color="error" onClick={() => setDeleteConfirmId(env.id)}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingId ? t('sapEnvironments.editEnvironment') : t('sapEnvironments.addEnvironment')}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            fullWidth
                            label={t('sapEnvironments.fields.identifier')}
                            value={form.identifier}
                            onChange={(e) => updateForm('identifier', e.target.value)}
                            required
                            placeholder="S4H_PROD"
                            error={identifierError}
                            helperText={identifierError ? t('sapEnvironments.identifierError') : t('sapEnvironments.identifierHint')}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title={t('sapEnvironments.identifierHelp')}>
                                                <IconButton onClick={() => setHelpOpen(true)} edge="end" size="small">
                                                    <HelpOutline fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                    sx: { fontFamily: 'monospace', fontWeight: 700 },
                                },
                            }}
                        />
                        <TextField
                            fullWidth label={t('sapEnvironments.fields.description')}
                            value={form.description} onChange={(e) => updateForm('description', e.target.value)}
                            required placeholder={t('sapEnvironments.placeholders.description')}
                        />
                        <TextField
                            fullWidth label={t('sapEnvironments.fields.host')}
                            value={form.host} onChange={(e) => updateForm('host', e.target.value)}
                            required placeholder={t('sapEnvironments.placeholders.host')}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth label={t('sapEnvironments.fields.client')}
                                value={form.client} onChange={(e) => updateForm('client', e.target.value)}
                                required placeholder="100"
                            />
                            <FormControl fullWidth>
                                <InputLabel>{t('sapEnvironments.fields.language')}</InputLabel>
                                <Select
                                    value={form.language}
                                    label={t('sapEnvironments.fields.language')}
                                    onChange={(e) => updateForm('language', e.target.value)}
                                >
                                    <MenuItem value="EN">EN - English</MenuItem>
                                    <MenuItem value="PT">PT - Português</MenuItem>
                                    <MenuItem value="DE">DE - Deutsch</MenuItem>
                                    <MenuItem value="ES">ES - Español</MenuItem>
                                    <MenuItem value="FR">FR - Français</MenuItem>
                                    <MenuItem value="IT">IT - Italiano</MenuItem>
                                    <MenuItem value="JA">JA - 日本語</MenuItem>
                                    <MenuItem value="KO">KO - 한국어</MenuItem>
                                    <MenuItem value="ZH">ZH - 中文</MenuItem>
                                    <MenuItem value="RU">RU - Русский</MenuItem>
                                    <MenuItem value="PL">PL - Polski</MenuItem>
                                    <MenuItem value="NL">NL - Nederlands</MenuItem>
                                    <MenuItem value="TR">TR - Türkçe</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <TextField
                            fullWidth label={t('sapEnvironments.fields.username')}
                            value={form.username} onChange={(e) => updateForm('username', e.target.value)}
                            required
                        />
                        <TextField
                            fullWidth
                            label={editingId ? t('sapEnvironments.fields.newPassword') : t('sapEnvironments.fields.password')}
                            type={showPassword ? 'text' : 'password'}
                            value={form.password} onChange={(e) => updateForm('password', e.target.value)}
                            required={!editingId}
                            helperText={editingId ? t('sapEnvironments.passwordHint') : ''}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
                    <Button
                        variant="contained" onClick={handleSubmit}
                        disabled={saving || !isFormValid}
                    >
                        {saving ? t('common.loading') : t('common.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Identifier Help Dialog */}
            <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{t('sapEnvironments.identifierHelpTitle')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {t('sapEnvironments.identifierHelpText')}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('sapEnvironments.identifierHelpExamples')}
                    </Typography>
                    <Box sx={{ fontFamily: 'monospace', fontSize: '0.9rem', bgcolor: 'action.hover', p: 1.5, borderRadius: 1 }}>
                        S4H_PROD<br />
                        NUMEN_DEV<br />
                        ECC_QAS<br />
                        BW4_SANDBOX
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {t('sapEnvironments.identifierHelpUsage')}
                    </Typography>
                    <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem', bgcolor: 'action.hover', p: 1.5, borderRadius: 1, mt: 1 }}>
                        adt_switch_system({'{'} system: "S4H_PROD" {'}'})
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHelpOpen(false)}>{t('common.close')}</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
                <DialogTitle>{t('sapEnvironments.deleteConfirm.title')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('sapEnvironments.deleteConfirm.message')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmId(null)}>{t('common.cancel')}</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}>
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default SapEnvironmentsPage;
